import {
    Box,
    Typography,
    Stack,
    Grid,
    useTheme,
    alpha,
    Divider,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

interface HotkeyEntry {
    id: string;
    function: string;
    tool: string;
    input: string;
    action: string;
    subActionOf?: string;
}

const hotkeys: HotkeyEntry[] = [
    // ── Hiding patient information ──
    {
        id: 'toggle-sensitive-info',
        function: 'Toggle Sensitive Info',
        tool: 'Any tool selected',
        input: 'Ctrl + H',
        action: 'Show/hide sensitive patient information (e.g. name, ID)',
    },
    // ── Picking a Tool ──
    {
        id: 'tool-menu',
        function: 'Tool Menu',
        tool: 'Pointer over an image',
        input: 'Hold Q',
        action: "Open the current step's tools around the pointer. Move toward a tool and release Q to arm it; release Q without moving to close the menu. Each tool shows its key. A greyed-out tool is not armed, and a message says why. Inactive while typing in a text field, while a dialog is open or while a tool stroke is in progress.",
    },
    {
        id: 'tool-menu-tap',
        function: '',
        tool: '',
        input: 'Tap Q',
        action: 'Keep the menu open, then click a tool to arm it. Esc, a click in the centre or outside the menu, any other key, or Q again closes it.',
    },
    {
        id: 'structure-menu',
        function: 'Structure Menu',
        tool: 'Contouring and review steps, pointer over an image',
        input: 'Hold W',
        action: 'Open the structures of the current structure set beside the pointer, in the order of the structure list. Move to a structure and release W to select it; release W without moving to close the list. A hidden structure is shown when it is selected, and a search or type filter in the structure list that hides it is cleared. Scroll a long list with the mouse wheel. Not available while several structures are selected. Inactive while typing in a text field, while a dialog is open or while a tool stroke is in progress.',
    },
    {
        id: 'structure-menu-tap',
        function: '',
        tool: '',
        input: 'Tap W',
        action: 'Keep the list open, then click a structure to select it. Esc, a click outside the list, any other key, or W again closes it.',
    },
    {
        id: 'tool-keys-viewing',
        function: 'Viewing Tools',
        tool: 'Any step',
        input: 'P / Z / O / M',
        action: 'Arm Pan, Box Zoom, Rotate View or Measure (in the mode used last). Inactive while typing in a text field, while a dialog is open or while a tool stroke is in progress.',
    },
    {
        id: 'tool-keys-contouring',
        function: 'Contouring Tools',
        tool: 'Contouring and review steps',
        input: 'B / A / L / X / T / D / S / C',
        action: 'Arm Static Brush, Adaptive Brush, Line Draw, Erase, Transform Structure (Move POI for a point structure), Deform, Smoothing Brush or Cross-Section. A tool that cannot be used now is not armed, and a message says why.',
    },
    {
        id: 'tool-keys-registration',
        function: 'Registration Modes',
        tool: 'Registration step',
        input: 'T / Y',
        action: 'Arm Translation or Rotation. A mode that cannot be used now is not armed, and a message says why.',
    },
    // ── Navigation ──
    {
        id: 'navigate-scroll',
        function: 'Navigation',
        tool: 'Any tool selected',
        input: 'Mouse wheel OR Arrow keys (↑/↓) OR Page Up/Page Down',
        action: 'Scroll through slices in all 2D views',
    },
    {
        id: 'select-plane',
        function: 'Change Row Planes',
        tool: 'Any tool selected',
        input: 'R OR Ctrl + R',
        action: "Change the planes of one row (Transversal, Sagittal, Coronal): the row of the maximized tile, else of the tile under the pointer or last clicked, else the only image row. With no such row, with a panel maximized, or on a row that shows one plane, the key does nothing and says why. The plane button in the row's first tile does the same. Inactive while typing in a text field or while a tool stroke is in progress.",
    },
    {
        id: 'maximize-view',
        function: 'Maximize Tile',
        tool: 'Any tool selected',
        input: 'Maximize button (tile title bar)',
        action: 'Show a single tile full size. Click the button again or press Esc to restore the grid.',
    },
    {
        id: 'rotate-views',
        function: 'Rotate Views (Oblique)',
        tool: '',
        input: 'Left mouse button + drag',
        action: 'Rotate the plane views of the image together about the crosshair, spinning about the normal of the tile you drag in. The planes stay mutually perpendicular. Dragging within a small radius of the crosshair does nothing, since the rotation is undefined there. Tools that edit the displayed slice refuse a view turned away from the image slices.',
    },
    {
        id: 'rotate-views-reset',
        function: '',
        tool: '',
        input: 'Snap-back button (OBLIQUE readout) OR Reset views',
        action: 'Return the planes to their patient-aligned orientation. Snap-back changes only the rotation of that image, leaving the crosshair, pan and zoom as they were; Reset views clears zoom and crosshair as well, in every row. While the planes of an image are oblique, its row shows an OBLIQUE readout with the angles from the patient axes.',
    },
    {
        id: 'navigate-pan-global',
        function: 'Pan Image',
        tool: 'Any tool selected',
        input: 'Middle mouse button + drag',
        action: 'Move image within display area',
    },
    {
        id: 'navigate-pan-space',
        function: '',
        tool: '',
        input: 'Space + Left mouse button + drag (Cmd on Mac)',
        action: 'Move image within display area (alternative)',
    },
    // ── Pan Tool ──
    {
        id: 'navigate-pan-tool',
        function: 'Pan Tool',
        tool: '',
        input: 'Left mouse button + drag',
        action: 'Move image within display area (dedicated tool)',
    },
    {
        id: 'scale-bar',
        function: 'Scale Bar',
        tool: '',
        input: 'Pan tool options: ruler button',
        action: 'Show or hide the scale bar on every tile. Off by default.',
    },
    {
        id: 'navigate-pan-tool-select',
        function: '',
        tool: '',
        input: 'Left mouse button click (no drag)',
        action: 'Select a structure (if hovering over one)',
    },
    // ── Zoom ──
    {
        id: 'zoom',
        function: 'Zoom',
        tool: 'Any tool selected',
        input: 'Ctrl + Mouse wheel',
        action: 'Zoom in/out on image',
    },
    // ── Window/Level ──
    {
        id: 'wl-main',
        function: 'Window/Level Adjustment',
        tool: 'Any tool selected',
        input: 'Right mouse button + drag',
        action: 'Adjust image contrast (global)',
    },
    {
        id: 'wl-vertical',
        function: '',
        tool: '',
        input: 'Vertical mouse movement',
        action: 'Adjust window center (brightness)',
    },
    {
        id: 'wl-horizontal',
        function: '',
        tool: '',
        input: 'Horizontal mouse movement',
        action: 'Adjust window width (contrast)',
    },
    {
        id: 'wl-secondary',
        function: '',
        tool: '',
        input: 'Alt + Right mouse button + drag',
        action: 'Adjust secondary image window/level on a tile that blends the pair (when loaded). A tile that draws one image of the pair alone adjusts that image, with or without Alt.',
        subActionOf: 'wl-main',
    },
    {
        id: 'wl-esc',
        function: '',
        tool: '',
        input: 'Esc (during adjustment)',
        action: 'Cancel and restore previous window/level',
        subActionOf: 'wl-main',
    },
    {
        id: 'wl-histogram',
        function: 'Windowing Histogram',
        tool: 'Any tool selected',
        input: 'H',
        action: 'Show/hide the windowing histogram strip. Inactive while typing in a text field.',
    },
    // ── Brush size (shared by the circular tools) ──
    {
        id: 'brush-size',
        function: 'Brush Size',
        tool: 'Brush, Eraser, Smoothing Brush or Deform tool selected',
        input: 'Shift + Mouse wheel',
        action: 'Change the tool diameter under the cursor',
    },
    // ── Brush Tool ──
    {
        id: 'brush-draw',
        function: 'Brush Tool',
        tool: '',
        input: 'Left mouse button click / drag',
        action: 'Paint contour with the brush. 2D mode paints the current slice only and needs a view that follows an image slice; 3D paints a sphere of the brush diameter on any view.',
    },
    {
        id: 'brush-erase',
        function: '',
        tool: '',
        input: 'Alt + Left mouse button',
        action: 'Enable erase mode',
    },
    {
        id: 'brush-cancel',
        function: '',
        tool: '',
        input: 'Esc (during drawing)',
        action: 'Cancel current brush stroke',
    },
    // ── Line Draw Tool ──
    {
        id: 'line-draw',
        function: 'Line Draw Tool',
        tool: '',
        input: 'Left mouse button click / drag',
        action: 'Draw contour point-by-point or freeform',
    },
    {
        id: 'line-erase-toggle',
        function: '',
        tool: '',
        input: 'E',
        action: 'Toggle between draw and erase mode. Inactive while typing in a text field.',
    },
    {
        id: 'line-close',
        function: '',
        tool: '',
        input: 'Left mouse button double-click',
        action: 'Close the current line drawing (connect to start point)',
    },
    {
        id: 'line-undo',
        function: '',
        tool: '',
        input: 'Right mouse button click',
        action: 'Undo last drawn segment',
    },
    {
        id: 'line-shift',
        function: '',
        tool: '',
        input: 'Hold Shift',
        action: 'Disable snap-to-close (draw through start point)',
    },
    {
        id: 'line-cancel',
        function: '',
        tool: '',
        input: 'Esc (during drawing)',
        action: 'Discard current line drawing',
    },
    // ── Eraser Tool ──
    {
        id: 'eraser',
        function: 'Eraser Tool',
        tool: '',
        input: 'Left mouse button click / drag',
        action: 'Erase contours point-by-point or freeform. 2D mode erases the current slice only and needs a view that follows an image slice; 3D erases a sphere of the brush diameter on any view.',
    },
    {
        id: 'eraser-cancel',
        function: '',
        tool: '',
        input: 'Esc (during erasing)',
        action: 'Cancel current erasing action',
    },
    // ── Transform Tool ──
    {
        id: 'transform',
        function: 'Transform Tool',
        tool: '',
        input: 'Left mouse button click + drag',
        action: 'Translate, resize/scale, or rotate the structure depending on where you click and drag',
    },
    // ── Line Deform Tool ──
    {
        id: 'line-deform',
        function: 'Line Deform Tool',
        tool: '',
        input: 'Left mouse button click + drag',
        action: 'Click and drag to deform line',
    },
    {
        id: 'line-deform-cancel',
        function: '',
        tool: '',
        input: 'Esc (during deformation)',
        action: 'Cancel current deformation and revert to previous line shape',
    },
    {
        id: 'line-deform-shift',
        function: '',
        tool: '',
        input: 'Shift + Mouse wheel',
        action: 'Adjust percentage of contour length to deform (when deforming)',
    },
    // ── Deform Tool ──
    {
        id: 'deform',
        function: 'Deform Tool',
        tool: '',
        input: 'Left mouse button + drag',
        action: 'Drag the structure surface to deform it. 2D mode deforms the current slice only; 3D deforms volumetrically around the cursor.',
    },
    {
        id: 'deform-cancel',
        function: '',
        tool: '',
        input: 'Esc (during deformation)',
        action: 'Cancel the current deform stroke',
    },
    // ── Smoothing Brush Tool ──
    {
        id: 'smooth-brush',
        function: 'Smoothing Brush Tool',
        tool: '',
        input: 'Left mouse button click / drag',
        action: 'Smooth the contour under the brush; the effect accumulates while dragging. 2D mode smooths the current slice only; 3D smooths a short range across slices.',
    },
    {
        id: 'smooth-brush-cancel',
        function: '',
        tool: '',
        input: 'Esc (during stroke)',
        action: 'Cancel the current smoothing stroke',
    },
    // ── Registration Tool ──
    {
        id: 'registration-swap',
        function: 'Registration Tool',
        tool: '',
        input: 'Hold Shift',
        action: 'Temporarily swap rotation/translation mode',
    },
    {
        id: 'registration-cancel',
        function: '',
        tool: '',
        input: 'Esc (during action)',
        action: 'Cancel and revert to previous translation/rotation',
    },
    {
        id: 'registration-translate-keys',
        function: '',
        tool: '',
        input: 'Ctrl + Arrow keys (Cmd on Mac)',
        action: 'Translate secondary image 0.5 mm per press (Translation mode)',
        subActionOf: 'registration-swap',
    },
    {
        id: 'registration-rotate-keys',
        function: '',
        tool: '',
        input: 'Ctrl + ←/→ (Cmd on Mac)',
        action: 'Rotate secondary image ~0.5° per press (Rotation mode)',
        subActionOf: 'registration-swap',
    },
    // ── Measure Tool ──
    {
        id: 'measure-modes',
        function: 'Measure Tool',
        tool: 'Any step',
        input: 'M',
        action: 'Measure in the mode used last: distance, angle, line profile, circle or rectangle, picked in the tray. The circle and the rectangle are not offered on the dose step, while a dose is shown or while the image is incomplete, and the tray says why.',
    },
    {
        id: 'measure-cycle',
        function: '',
        tool: 'Measure tool selected',
        input: 'Shift + Mouse wheel',
        action: 'Step to the next or previous mode (distance, angle, profile, circle, rectangle) instead of scrolling the slice. A refused circle or rectangle is passed over.',
        subActionOf: 'measure-modes',
    },
    {
        id: 'measure-deselect',
        function: '',
        tool: '',
        input: 'Esc',
        action: 'While a shape is being drawn: cancel it. Otherwise: clear the selection of the measurement selected on the image. While measuring or under Pan that is all it does; a second Esc then closes an open panel or restores a maximized tile.',
        subActionOf: 'measure-modes',
    },
    {
        id: 'measure-delete',
        function: '',
        tool: '',
        input: 'Delete OR Backspace',
        action: 'Delete the measurement under the pointer, at once. With a measurement under the pointer, the key deletes it and nothing else: no structure is deleted or cleared.',
        subActionOf: 'measure-modes',
    },
    // ── Structure Management ──
    {
        id: 'structure-management',
        function: 'Structure Management',
        tool: 'Structure selected',
        input: 'Delete (Fn + Backspace on Mac)',
        action: 'Brush, line tool, eraser and line deform: remove contours on current slice; Other tools: open confirmation dialog to delete the entire structure',
    },
    {
        id: 'structure-management-shift',
        function: '',
        tool: '',
        input: 'Shift + Delete',
        action: 'Other tools only: delete the entire structure immediately (skip confirmation dialog)',
        subActionOf: 'structure-management',
    },
    // ── Peek Under Structure ──
    {
        id: 'peek-under-structure',
        function: 'Peek Under Structure',
        tool: 'Structure selected',
        input: 'Hold V',
        action: 'Hide the contour of the selected structure (and its adjacent-slice ghosts) in the 2D views while held, to show the image beneath it; it comes back on release. Changes nothing else: the visibility setting, the selection and the contour data stay as they were, and contour tools and undo do not act on it while held. Inactive while typing in a text field, while a dialog is open, during a contour edit, for point structures, for a structure being compared and while several structures are selected.',
    },
    // ── Undo / Redo ──
    {
        id: 'undo',
        function: 'Undo / Redo',
        tool: 'Structure selected (contouring and review steps)',
        input: 'Ctrl + Z',
        action: 'Undo the last change to the selected structure. The undo button flashes to confirm. Ctrl (not Cmd) on every platform, including Mac. Inactive while typing in a text field, while a dialog is open, while a tool stroke is in progress, and for approved structures. Holding the keys does not repeat — one press is one step.',
    },
    {
        id: 'redo',
        function: '',
        tool: '',
        input: 'Ctrl + Y',
        action: 'Redo the last undone change to the selected structure.',
        subActionOf: 'undo',
    },
    {
        id: 'undo-history',
        function: '',
        tool: '',
        input: 'History button (contouring toolbar)',
        action: 'Open the selected structure\u2019s edit history to jump to any retained step, or use "Undo all" to return it to the state before the first edit of the session.',
        subActionOf: 'undo',
    },
    // ── 3D Preview ──
    {
        id: 'preview-3d-rotate',
        function: '3D Preview',
        tool: '3D preview panel open',
        input: 'Left mouse button + drag',
        action: 'Rotate the 3D preview of the selected structure',
    },
    {
        id: 'preview-3d-spin',
        function: '',
        tool: '',
        input: 'Alt + Left mouse button + drag',
        action: 'Spin the 3D preview in place about the view axis (in-plane roll)',
        subActionOf: 'preview-3d-rotate',
    },
    {
        id: 'preview-3d-zoom',
        function: '',
        tool: '',
        input: 'Mouse wheel',
        action: 'Zoom the 3D preview in/out',
        subActionOf: 'preview-3d-rotate',
    },
];

function renderInput(input: string): JSX.Element {
    return (
        <Box
            component="span"
            sx={(theme) => ({
                display: 'inline-block',
                fontFamily: 'monospace',
                fontSize: 12,
                backgroundColor: alpha(theme.palette.primary.light, 0.15),
                color: theme.palette.primary.light,
                border: `1px solid ${alpha(theme.palette.primary.light, 0.25)}`,
                borderRadius: '6px',
                px: 0.75,
                whiteSpace: 'pre-wrap',
            })}
        >
            {input}
        </Box>
    );
}

export default function HotkeysHelp() {
    const theme = useTheme();

    return (
        <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
            <Stack
                spacing={2}
                sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color="primary.light"
                        >
                            <FormattedMessage id="shortcutsAndControls" />
                        </Typography>
                    </Box>

                    <Divider
                        sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {hotkeys.map((entry) => (
                        <Grid container spacing={2} key={entry.id}>
                            <Grid item xs={6}>
                                <Typography fontWeight={500} fontSize={14}>
                                    {entry.function}
                                </Typography>
                                {entry.tool && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        {entry.tool}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={6}>
                                {renderInput(entry.input)}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {entry.action}
                                </Typography>
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
}
